import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calculator, ChevronRight, TrendingUp, Building2, Users, Briefcase, ArrowRight, Scale, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';

const BUSINESS_TYPES = [
  'Uslugi IT / programowanie',
  'Handel detaliczny',
  'Handel hurtowy',
  'Uslugi budowlane',
  'Uslugi transportowe',
  'Gastronomia',
  'Uslugi medyczne',
  'Doradztwo / consulting',
  'Uslugi prawne',
  'Produkcja',
  'Inne uslugi',
];

const LEGAL_FORMS = [
  { value: 'JDG', label: 'Jednoosobowa dzialalnosc gospodarcza (JDG)' },
  { value: 'SC', label: 'Spolka cywilna' },
  { value: 'SJ', label: 'Spolka jawna' },
  { value: 'SP_ZOO', label: 'Spolka z o.o.' },
];

const formatMarkdown = (text) => {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    // Tables
    .replace(/\|(.+)\|/g, (match) => {
      const cells = match.split('|').filter(c => c.trim() !== '');
      const isSeparator = cells.every(c => /^[\s\-:]+$/.test(c));
      if (isSeparator) return '<!--sep-->';
      const isHeader = false;
      const cellsHtml = cells.map(c => `<td class="px-3 py-2.5 text-sm border-b border-slate-100 dark:border-slate-700">${c.trim()}</td>`).join('');
      return `<tr class="hover:bg-slate-50/80 dark:hover:bg-slate-700/30 transition-colors">${cellsHtml}</tr>`;
    })
    // Headers
    .replace(/^#### (.+)$/gm, '<h4 class="text-sm font-bold text-slate-700 dark:text-slate-200 mt-5 mb-2 flex items-center gap-1.5">$1</h4>')
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-bold text-slate-800 dark:text-slate-100 mt-6 mb-2.5 pb-1.5 border-b border-slate-200 dark:border-slate-600">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold text-[#1e3a5f] dark:text-blue-300 mt-7 mb-3 pb-2 border-b-2 border-green-500/30">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-xl font-extrabold text-[#1e3a5f] dark:text-white mt-4 mb-4">$1</h1>')
    // Bold + Italic
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-800 dark:text-white">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Lists
    .replace(/^- (.+)$/gm, '<li class="ml-1 pl-3 py-0.5 text-sm text-slate-700 dark:text-slate-300 relative before:content-[\'\\2022\'] before:absolute before:-left-1 before:text-green-500 before:font-bold">$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-1 pl-3 py-0.5 text-sm text-slate-700 dark:text-slate-300 list-decimal">$2</li>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>')
    // Remove separator markers
    .replace(/<!--sep-->\n?/g, '')
    // Line breaks
    .replace(/\n\n/g, '</p><p class="mb-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">')
    .replace(/\n/g, '<br/>');

  html = '<p class="mb-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">' + html + '</p>';
  // Wrap table rows
  html = html.replace(/(<tr[^>]*>.*?<\/tr>(\s*<tr[^>]*>.*?<\/tr>)*)/gs, (match) => {
    const rows = match.match(/<tr[^>]*>.*?<\/tr>/gs) || [];
    if (rows.length === 0) return match;
    const firstRow = rows[0].replace(/<td/g, '<th').replace(/<\/td/g, '</th').replace(/class="[^"]*"/g, 'class="px-3 py-2.5 text-xs font-bold text-left text-slate-600 dark:text-slate-300 uppercase tracking-wider bg-slate-50 dark:bg-slate-700/50 border-b-2 border-green-500/20"');
    const bodyRows = rows.slice(1).join('');
    return `<div class="overflow-x-auto my-4 rounded-xl border border-slate-200 dark:border-slate-600 shadow-sm"><table class="w-full border-collapse"><thead>${firstRow}</thead><tbody>${bodyRows}</tbody></table></div>`;
  });
  // Wrap list items in ul
  html = html.replace(/(<li[^>]*>.*?<\/li>)+/gs, '<ul class="space-y-0.5 my-3">$&</ul>');
  return html;
};

export default function TaxComparisonPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    revenue: '',
    costs: '',
    employees: '0',
    business_type: 'Uslugi IT / programowanie',
    legal_form: 'JDG',
  });
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCompare = async () => {
    if (!form.revenue) { toast.error('Podaj przychod roczny'); return; }
    setLoading(true);
    setResult('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BACKEND_URL}/api/tax-comparison`, {
        revenue: parseFloat(form.revenue.replace(/\s/g, '')) || 0,
        costs: parseFloat(form.costs.replace(/\s/g, '')) || 0,
        employees: parseInt(form.employees) || 0,
        business_type: form.business_type,
        legal_form: form.legal_form,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data.comparison);
    } catch { toast.error('Blad porownania'); }
    finally { setLoading(false); }
  };

  const formatNumber = (val) => {
    const num = val.replace(/[^\d]/g, '');
    return num ? parseInt(num).toLocaleString('pl-PL') : '';
  };

  return (
    <div className="min-h-screen bg-[#f8fafb] dark:bg-slate-900">
      {/* Header */}
      <div className="border-b bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard" className="text-slate-500 hover:text-slate-800">
            <ChevronRight className="w-4 h-4 rotate-180" /> Powrot
          </Button>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-sm">
              <Scale className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-slate-800 dark:text-white">Porownywarka form opodatkowania</h1>
              <p className="text-xs text-slate-400">AI analizuje najkorzystniejsza forme dla Twojej firmy</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Input Form */}
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 sticky top-20">
              <h2 className="font-bold text-sm text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-green-600" /> Dane firmy
              </h2>
              <div className="space-y-3.5">
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                    Przychod roczny (PLN)
                  </label>
                  <div className="relative">
                    <TrendingUp className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-green-500" />
                    <input
                      data-testid="input-revenue"
                      type="text"
                      value={form.revenue}
                      onChange={e => setForm({ ...form, revenue: formatNumber(e.target.value) })}
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm focus:ring-2 focus:ring-green-500/30 focus:border-green-400 transition-all"
                      placeholder="np. 500 000"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Koszty roczne (PLN)</label>
                  <input
                    data-testid="input-costs"
                    type="text"
                    value={form.costs}
                    onChange={e => setForm({ ...form, costs: formatNumber(e.target.value) })}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm focus:ring-2 focus:ring-green-500/30 focus:border-green-400 transition-all"
                    placeholder="np. 100 000"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                    Liczba pracownikow
                  </label>
                  <div className="relative">
                    <Users className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="input-employees"
                      type="number"
                      min="0"
                      value={form.employees}
                      onChange={e => setForm({ ...form, employees: e.target.value })}
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm focus:ring-2 focus:ring-green-500/30 focus:border-green-400 transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Rodzaj dzialalnosci</label>
                  <select
                    data-testid="select-business-type"
                    value={form.business_type}
                    onChange={e => setForm({ ...form, business_type: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm focus:ring-2 focus:ring-green-500/30 focus:border-green-400 transition-all"
                  >
                    {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                    Forma prawna
                  </label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <select
                      data-testid="select-legal-form"
                      value={form.legal_form}
                      onChange={e => setForm({ ...form, legal_form: e.target.value })}
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm focus:ring-2 focus:ring-green-500/30 focus:border-green-400 transition-all"
                    >
                      {LEGAL_FORMS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <Button
                data-testid="compare-tax-btn"
                onClick={handleCompare}
                disabled={loading}
                className="w-full mt-5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white h-11 rounded-lg shadow-sm shadow-green-500/20"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Obliczam...
                  </span>
                ) : (
                  <span className="flex items-center gap-2"><ArrowRight className="w-4 h-4" /> Porownaj</span>
                )}
              </Button>
            </div>
          </div>

          {/* Result */}
          <div className="lg:col-span-8 xl:col-span-9">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              {result ? (
                <div>
                  <div className="px-5 py-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 border-b border-green-200/50 dark:border-green-800/30 flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-green-600" />
                    <h2 className="font-bold text-sm text-green-800 dark:text-green-300">Wyniki porownania form opodatkowania</h2>
                  </div>
                  <ScrollArea className="h-[calc(100vh-180px)]">
                    <div
                      data-testid="tax-comparison-result"
                      className="p-5 lg:p-6"
                      dangerouslySetInnerHTML={{ __html: formatMarkdown(result) }}
                    />
                  </ScrollArea>
                </div>
              ) : (
                <div className="h-[calc(100vh-180px)] flex items-center justify-center">
                  <div className="text-center max-w-sm">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 flex items-center justify-center mx-auto mb-5">
                      <Scale className="w-10 h-10 text-green-500/50" />
                    </div>
                    <h3 className="font-bold text-slate-700 dark:text-slate-200 mb-2">Wprowadz dane firmy</h3>
                    <p className="text-sm text-slate-400 mb-6">AI przeanalizuje i porowna najkorzystniejsze formy opodatkowania dla Twojej dzialalnosci</p>
                    <div className="grid grid-cols-2 gap-2">
                      {['Ryczalt', 'Skala podatkowa', 'Liniowy 19%', 'Estonski CIT'].map(f => (
                        <div key={f} className="px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-700/50 text-xs font-medium text-slate-500 dark:text-slate-400 border border-slate-100 dark:border-slate-600">
                          {f}
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 flex items-start gap-2 p-3 bg-blue-50/80 dark:bg-blue-900/10 rounded-lg text-left">
                      <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-600 dark:text-blue-400">Obliczenia uwzgledniaja aktualne progi, stawki ZUS, skladke zdrowotna i ulgi podatkowe na 2025/2026.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
